/**
 * Chat navigation helpers — shared across pages that link into the companion
 * or Williams (/trfer/williams) full-page chat.
 *
 * #2266: the companion conversation lives on the parent home. A page that
 * hands the companion a prompt goes to `/dashboard` with the prompt in router
 * STATE, never in the URL, so a refresh or a shared link cannot resend it; the
 * home sends it on arrival and clears the state.
 *
 * The `new=true` param forces a fresh conversation on entry to `/chat`, so a
 * link never deposits the user inside whatever thread happened to be most
 * recent. ChatPage consumes it on mount.
 */

import type { NavigateFunction } from 'react-router-dom';

/** The router state `navigateToStevens` carries to the home. */
export interface StevensPrefillState {
  stevensPrefill: string;
}

/** The prompt a `navigateToStevens` hop carried, or null. */
export function readStevensPrefill(state: unknown): string | null {
  if (!state || typeof state !== 'object') return null;
  const value = (state as Partial<StevensPrefillState>).stevensPrefill;
  return typeof value === 'string' && value.trim() ? value : null;
}

/**
 * Take the family to the companion on the home with a prompt it sends on
 * arrival — a fresh thread, because the home opens a new one on every visit.
 */
export function navigateToStevens(navigate: NavigateFunction, prefill: string): void {
  const state: StevensPrefillState = { stevensPrefill: prefill };
  navigate('/dashboard', { state });
}

/** Navigate to an empty text chat — fresh conversation, no prefill, welcome screen. */
export function navigateToStevensTextFresh(navigate: NavigateFunction): void {
  navigate('/chat?mode=text&new=true');
}

/** Navigate to the companion and auto-start the voice session — fresh conversation. */
export function navigateToStevensVoice(navigate: NavigateFunction): void {
  navigate('/chat?voice=true&new=true');
}

/** Navigate to Williams text chat with a prefilled, auto-sent message. */
export function navigateToWilliams(navigate: NavigateFunction, prefill: string): void {
  navigate(`/trfer/williams?prefill=${encodeURIComponent(prefill)}&autoopen=true`);
}

/** Navigate to an empty Williams text chat — fresh conversation, no prefill. */
export function navigateToWilliamsTextFresh(navigate: NavigateFunction): void {
  navigate('/trfer/williams?new=true');
}

/** Navigate to Williams and auto-start the voice session — fresh conversation. */
export function navigateToWilliamsVoice(navigate: NavigateFunction): void {
  navigate('/trfer/williams?voice=true&new=true');
}
